import { useCallback, useState } from "react";

import { WorkspacesService, type Workspace } from "@/api";
import { useMutation } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/ui/form-field";

interface Props {
  workspace: Workspace | null;
  /** All workspaces (used by ``reasonCannotArchive`` to validate
   *  the last-active + default guardrails before the call). */
  allWorkspaces: ReadonlyArray<Workspace>;
  onClose: () => void;
  onArchived: () => void;
}

/** Archive workspace via type-name-confirm. Two guardrails fire
 *  before the destructive button enables:
 *  1. Cannot archive the default workspace.
 *  2. Cannot archive the last active workspace.
 *  Both are mirrored at the backend (``CannotArchiveDefaultWorkspaceError``);
 *  surfacing them client-side keeps the dialog from sending a
 *  request that will 409 anyway. */
export function ArchiveWorkspaceDialog({
  workspace,
  allWorkspaces,
  onClose,
  onArchived,
}: Props) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState("");

  const mutator = useCallback(
    (id: string) => WorkspacesService.archive(id),
    []
  );
  const { mutate, isLoading, error, reset } = useMutation<string, void>(
    mutator,
    { onSuccess: () => onArchived() }
  );

  if (!workspace) return null;

  // Mirrors ``reasonCannotArchive`` (workspace-helpers) but resolves
  // the guardrail message through i18n so it follows the locale.
  let blockedReason: string | null = null;
  if (workspace.is_default) {
    blockedReason = t("workspaces.archiveDialog.blockedDefault");
  } else {
    const activeOthers = allWorkspaces.filter(
      (w) => w.status === "active" && w.id !== workspace.id
    );
    if (activeOthers.length === 0) {
      blockedReason = t("workspaces.archiveDialog.blockedLastActive");
    }
  }
  const matchesName = typed === workspace.name;
  const canSubmit = matchesName && blockedReason === null && !isLoading;

  const handleClose = () => {
    reset();
    setTyped("");
    onClose();
  };

  const handleConfirm = async () => {
    if (!canSubmit) return;
    await mutate(workspace.id);
    setTyped("");
  };

  return (
    <Dialog open onClose={handleClose} size="max-w-md">
      <DialogHeader>
        <DialogTitle>{t("workspaces.archiveDialog.title")}</DialogTitle>
        <DialogDescription>
          {t("workspaces.archiveDialog.description")}
        </DialogDescription>
      </DialogHeader>

      <DialogBody>
        {blockedReason && (
          <Alert variant="warning">
            <div className="text-xs">{blockedReason}</div>
          </Alert>
        )}

        <div className="rounded-md border border-ocean-100 bg-ocean-50/40 p-3 text-sm">
          <div className="label-eyebrow mb-1">
            {t("workspaces.archiveDialog.workspaceLabel")}
          </div>
          <div className="font-medium text-ocean-900">{workspace.name}</div>
          <div className="mt-1 text-xs text-neutral-600">
            {t("workspaces.archiveDialog.slugLabel")}{" "}
            <code className="text-ocean-700">{workspace.slug}</code>
            {workspace.is_default && (
              <span className="ml-2 text-warning-600">
                {" "}
                {t("workspaces.archiveDialog.defaultSuffix")}
              </span>
            )}
          </div>
        </div>

        {!blockedReason && (
          <FormField
            label={t("workspaces.archiveDialog.confirmLabel", {
              name: workspace.name,
            })}
            value={typed}
            onChange={setTyped}
            autoFocus
            autoComplete="off"
            hint={t("workspaces.archiveDialog.confirmHint")}
          />
        )}

        {error && (
          <Alert variant="danger" className="mt-3">
            {parseApiError(error)}
          </Alert>
        )}
      </DialogBody>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={handleClose}
          disabled={isLoading}
        >
          {t("workspaces.archiveDialog.cancel")}
        </Button>
        <Button
          type="button"
          variant="danger"
          onClick={handleConfirm}
          disabled={!canSubmit}
        >
          {isLoading
            ? t("workspaces.archiveDialog.submitLoading")
            : t("workspaces.archiveDialog.submit")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
